import { motion } from 'framer-motion'
import type { ReactNode } from 'react'

import type { PlanPriceCopy } from '@/customer/components/billing/subscription-plans'
import { cn } from '@/customer/lib/utils'

/**
 * Selectable membership plan card, shared by the plan picker and the upgrade page so prices
 * and discounts read the same way in both. Callers own the pricing; `children` holds their
 * extras below the price block.
 */
export function PlanOptionCard({
  name,
  value,
  title,
  pricing,
  fullPrice,
  discountNote,
  checked,
  onSelect,
  selectedClassName = 'bg-baby-blue',
  children,
}: {
  /** Radio group name. */
  name: string
  value: string
  title: string
  pricing: PlanPriceCopy
  /** Undiscounted per-month price, struck through beside a discounted one. */
  fullPrice?: string
  /** Names the discount behind `fullPrice`, e.g. "10% multi-pet discount". */
  discountNote?: string
  checked: boolean
  onSelect: () => void
  selectedClassName?: string
  children?: ReactNode
}) {
  return (
    <label
      className={cn(
        'flex flex-1 cursor-pointer flex-col rounded-lg border-2 border-foreground p-4 transition-colors',
        checked ? selectedClassName : 'bg-neutral-100 opacity-60 hover:bg-neutral-300'
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onSelect}
        className="sr-only"
      />

      {/* Matches the `CardRadioGroup` bounce; `initial={false}` skips it for the card selected
          on mount. */}
      <motion.h3
        className="text-headline-sm origin-left"
        initial={false}
        animate={checked ? 'selected' : 'idle'}
        variants={{
          idle: { scale: 1 },
          selected: { scale: [1, 1.15, 0.95, 1.06, 1] },
        }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        {title}
      </motion.h3>

      <div className="mt-3">
        <p className="flex items-baseline gap-1">
          {fullPrice ? <s className="text-label-md text-foreground/60">{fullPrice}</s> : null}
          <span className="text-headline-md leading-none">{pricing.price}</span>
          <span className="text-label-md text-foreground/70">/mo</span>
        </p>

        {/* Cadence and commitment sit right under the price so they read as part of it. */}
        <div className="mt-1 text-fine-print text-foreground/60">
          <p>{pricing.billing}</p>
        </div>

        {pricing.savings ? <p className="mt-2 font-bold">{pricing.savings}</p> : null}
        {discountNote ? <p className="mt-1 font-bold">{discountNote}</p> : null}
      </div>

      {children}
    </label>
  )
}
